/*
 |--------------------------------------------------------------------------
 | Popover
 |
 | This module adds functionality to the Sitecore and Angular version of
 | the popover component
 |--------------------------------------------------------------------------
 */

// Constants
const APopoverSelector = '.a-popover';
const APopoverOpenAttribute = 'open';
const APopoverTargetAttribute = 'data-popover-target';

// Class
class APopOver {
  constructor(element) {
    this.element = element;
  }

  init = () => {
    const id = this.element.id;
    if (!id) {
      return;
    }

    this.trigger = document.querySelector(
      `[${APopoverTargetAttribute}="${id}"]`
    );
    if (!this.trigger) {
      return;
    }

    this.position(this.trigger);

    new ResizeObserver(() => {
      this.position(this.trigger);
    }).observe(document.body);

    /**
     * Close on scroll
     */
    window.addEventListener(
      'scroll',
      (event) => {
        if (this.isOutside(event.target) && this.isVisible()) {
          this.hide();
        }
      },
      true
    );

    // Open/close popover on trigger click
    this.trigger.addEventListener('click', () => {
      this.toggle();
      if (this.isVisible()) {
        this.position(this.trigger);
      }
    });

    // Close on clicks that are not inside popover or trigger
    window.addEventListener('click', (event) => {
      if (this.isOutside(event.target) && this.isVisible()) {
        this.hide();
      }
    });

    /*
    Fix specifically for cases where popover is inside a drawer.
    Because drawers are closed on page load we need to trigger positioning when a drawer is opened.
    */
    document.querySelectorAll('dialog.a-drawer').forEach((drawer) => {
      drawer.addEventListener('drawerOpen', () => this.position(this.trigger));
    });
  };

  toggle = () => {
    if (this.isVisible()) {
      this.hide();
    } else {
      this.show();
    }
  };
  isVisible = () => this.element.hasAttribute(APopoverOpenAttribute);
  isOutside = (element) =>
    !this.element.contains(element) && !this.trigger.contains(element);
  show = () => {
    this.element.setAttribute(APopoverOpenAttribute, '');
    this.element.dispatchEvent(new Event('popoverOpened'));
  };
  hide = () => {
    this.element.removeAttribute(APopoverOpenAttribute);
    this.element.dispatchEvent(new Event('popoverClosed'));
  };

  position = (trigger) => {
    const triggerRect = trigger.getBoundingClientRect();
    const popoverPos = {
      x: triggerRect.left,
      y: triggerRect.bottom,
    };

    // Get dimensions by quickly showing and hiding element, if hidden
    const hidden = !this.isVisible();
    if (hidden) {
      this.show();
    }
    const elementDimensions = {
      width: this.element.offsetWidth,
      height: this.element.offsetHeight,
    };
    if (hidden) {
      this.hide();
    }

    if (triggerRect.right + elementDimensions.width > window.innerWidth) {
      popoverPos.x = triggerRect.right - elementDimensions.width;
    }
    if (
      triggerRect.bottom + elementDimensions.height > window.innerHeight &&
      triggerRect.top - elementDimensions.height > 0 // only if element truly fits
    ) {
      popoverPos.y = triggerRect.top - elementDimensions.height;
    }

    this.element.style.setProperty(
      '--_a-popout-position-x',
      `${popoverPos.x}px`
    );
    this.element.style.setProperty(
      '--_a-popout-position-y',
      `${popoverPos.y}px`
    );
  };
}

// Global show popover function
window.showPopover = (popoverId) => {
  popover = document.getElementById(popoverId);
  if (popover === null) {
    throw new Error('No popover found with id="' + popoverId + '".');
  }

  popover.popoverClass.show();
};

// Global close popover function
window.closePopover = (popoverId) => {
  popover = document.getElementById(popoverId);
  if (popover === null) {
    throw new Error('No popover found with id="' + popoverId + '".');
  }

  popover.popoverClass.hide();
};

// Initiate module
function APopOverCreateInstance(element) {
  if (!element.popoverClass) {
    element.popoverClass = new APopOver(element);
    element.popoverClass.init();
  }
}

// Create new Popover instances on DOM mutation
document
  .querySelectorAll(`${APopoverSelector}`)
  .forEach(APopOverCreateInstance);

const APopOverObserver = new MutationObserver(function (mutations) {
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((element) => {
      document
        .querySelectorAll(`${APopoverSelector}`)
        .forEach(APopOverCreateInstance);

      // Close all popovers if a modal is opened
      const modals = document.querySelectorAll(
        '.modal:not([hidden]), .a-modal:not([hidden])'
      );
      if (modals.length > 0) {
        document.querySelectorAll(`${APopoverSelector}`).forEach((popover) => {
          popover.popoverClass.hide();
        });
      }
    });
  });
});

APopOverObserver.observe(document, {
  attributes: false,
  childList: true,
  characterData: false,
  subtree: true,
});
